"use client";
import { Session, User, Subscription as SupabaseSubscription } from "@supabase/supabase-js";
import React, { createContext, useEffect, useState } from "react";
import { supabaseAnonClient } from "@/pages/api/utils/supabase/browser";
import { objectToCamel } from "ts-case-convert";
import { BBUser, Subscription } from "@/types";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

function gtagSendEvent() {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion_event_signup", {
      event_timeout: 2000,
    });
  }
}

interface AuthContextProps {
  user?: BBUser;
  isLoading: boolean;
  setUser: React.Dispatch<React.SetStateAction<any>>;
  login: ({
    phone,
    token,
    email,
    action,
  }: {
    email?: string;
    phone?: string;
    token: string;
    action?: string;
  }) => { error: any; success: boolean };
  logout: () => void;
  isLoggedIn: boolean;
  isAdmin: boolean;
  verify: (
    phone: string,
    otp: string
  ) => { error: any; success: boolean; user: User; session: Session };
  getUser: () => BBUser;
  checkSession: () => void;
}

export const AuthContext = createContext<AuthContextProps>({
  user: undefined,
  isLoading: true,
  setUser: () => {},
  login: () => {
    return { error: {}, success: false };
  },
  logout: () => {},
  verify: () => {
    return {
      error: {},
      success: false,
      user: {} as User,
      session: {} as Session,
    };
  },
  isLoggedIn: false,
  isAdmin: false,
  getUser: () => {
    return {} as BBUser;
  },
  checkSession: () => {},
});

// Module-level guard to ensure only one subscription
let hasSubscribedToAuth = false;
let loginEventTimeout: NodeJS.Timeout | null = null;

// Debounced login event function
const debouncedLoginEvent = (user: User | undefined) => {
  if (loginEventTimeout) {
    clearTimeout(loginEventTimeout);
  }
  loginEventTimeout = setTimeout(() => {
    posthog.capture('login', {
      loginMethod: 'google',
      $set: {
        lastLoginAt: new Date().toISOString()
      }
    });
    if(user) {
      posthog.identify(user.id, {
        email: user.email,
        phone: user.phone,
      });
    }
  }, 3000);
};

// True singleton subscription - exists outside React lifecycle
if (typeof window !== 'undefined' && !hasSubscribedToAuth) {
  hasSubscribedToAuth = true;
  const subscription = supabaseAnonClient.auth.onAuthStateChange((event, session) => {
    if(event === 'SIGNED_IN') {
      const provider = session?.user?.identities?.[0]?.provider;
      if(provider === 'google') {
        // TODO: use this for all login events
        debouncedLoginEvent(session?.user);
      }
    }
  });
  console.log('subscription', subscription);
}

export const AuthProvider = (props: any) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState<BBUser>();
  const router = useRouter();

  useEffect(() => {
    if (!isLoggedIn) {
      setIsAdmin(false);
    }
  }, [isLoggedIn]);

  // On app load, check for authRedirect in localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const redirectUrl = localStorage.getItem('authRedirect');
      if (redirectUrl) {
        localStorage.removeItem('authRedirect');
        router.replace(redirectUrl);
      }
    }
  }, []);

  const getLatestSubscription = async () => {
    const { data, error } = await supabaseAnonClient
      .from("user_subscription")
      .select("*")
      .gte("subscription_end_date", new Date().toISOString().split("T")[0])
      .order("subscription_end_date", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      return undefined;
    }
    return data;
  };
  const login = async ({
    phone,
    token,
    email,
    action,
  }: {
    email?: string;
    phone?: string;
    token: string;
    action?: string;
  }) => {
    const formData = new FormData();
    if (phone) {
      formData.append("phone", phone);
    }
    if (email) {
      formData.append("email", email);
    }
    formData.append("token", token);
    if (action) {
      formData.append("action", action);
    }

    const response = await fetch("/auth/login", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      return {
        error: "Login failed",
        success: false,
      };
    } else {
      if (response.redirected) {
        window.location.href = response.url;
      }
      return {
        success: true,
      };
    }
  };

  const verify = async (phone: string, otp: string) => {
    const formData = new FormData();
    formData.append("otp", otp);
    formData.append("phone", phone);
    const response = await fetch("/auth/verify", {
      method: "POST",
      body: formData,
      redirect: "manual",
    });
    setIsLoading(false);
    if (response.status >= 400) {
      return {
        error: "Login failed",
        success: false,
      };
    } else {
      const { user, session } = await response.json();
      const latestSub = await getLatestSubscription();
      setUser({
        ...user,
        subscription: latestSub ? objectToCamel(latestSub) : undefined,
      });
      setIsLoggedIn(true);
      gtagSendEvent();
      return {
        success: true,
        user,
        session,
      };
    }
  };

  async function checkSession() {
    const { data, error } = await supabaseAnonClient.auth.getUser();
    const latestSub = await getLatestSubscription();
    if (data && data.user) {
      setIsLoggedIn(true);
      if (data.user.user_metadata["isAdmin"]) {
        setIsAdmin(true);
      }
      const { user } = data;
      setUser({
        ...user,
        subscription: latestSub ? (objectToCamel(latestSub) as any) : undefined,
      });
    } else {
      console.log(error);
      setIsLoggedIn(false);
    }
    setIsLoading(false);
  }

  function getUser() {
    return user;
  }

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    setIsLoggedIn(!!user);
  }, [user]);

  const logout = async () => {
    await supabaseAnonClient.auth.signOut();
    setIsLoggedIn(false);
    window.location.href = "/";
  };

  const value = {
    isLoggedIn,
    isLoading,
    isAdmin,
    login,
    logout,
    verify,
    getUser,
    user,
    checkSession,
  };

  return <AuthContext.Provider value={value} {...props} />;
};
